import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, Phone, Mail,  Check, Shield, Clock, 
  Users, Award, 
} from 'lucide-react';
import serviceData from '../data/services.json';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const service = serviceData[serviceId];

  if (!service) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-32 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-6">Leistung nicht gefunden</h1>
          <p className="text-xl text-slate-600 mb-8">Die angeforderte Dienstleistung existiert nicht in unserem Portfolio.</p>
          <Link to="/" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-full font-bold transition-all">
            <ArrowLeft size={20} /> Zurück zur Startseite
          </Link>
        </div>
      </div>
    );
  }



  // Service benefits
  const benefits = [
    { icon: <Shield className="w-6 h-6" />, text: "Fachgerechte Ausführung durch Meisterbetrieb" },
    { icon: <Clock className="w-6 h-6" />, text: "Schnelle Reaktionszeit & Termintreue" },
    { icon: <Users className="w-6 h-6" />, text: "Persönliche Beratung & Betreuung" },
    { icon: <Award className="w-6 h-6" />, text: "5 Jahre Gewährleistung auf alle Arbeiten" },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div 
        className="relative text-white overflow-hidden bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/20069743/pexels-photo-20069743.jpeg?_gl=1*1xjr7m5*_ga*NDIwOTEzMDMyLjE3NjkzNTY0Mjk.*_ga_8JE65Q40S6*czE3NzA2NzI5NTYkbzIkZzEkdDE3NzA2NzMxNzkkajUzJGwwJGgw)'
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Text Content */}
            <div className="max-w-4xl">
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 text-emerald-300 hover:text-white mb-8 transition-colors group"
              >
                <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                Zurück zur Startseite
              </Link>
              
              <div className="flex items-center gap-4 mb-6">
             
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-black leading-tight">
                  {service.title}
                </h1>
              </div>
              
              <p className="text-xl md:text-2xl text-emerald-100 max-w-3xl mb-12 leading-relaxed">
                {service.description}
              </p>
              
              <div className="flex flex-wrap gap-4">
                <button className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-full font-bold flex items-center gap-3 transition-all hover:shadow-xl hover:shadow-emerald-900/30">
                  <Phone size={20} /> Jetzt Beratung anfragen
                </button>
                
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative hidden lg:block">
            
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Service Image */}
            <div className="rounded-3xl overflow-hidden mb-12 shadow-2xl">
              <img 
                src={service.image || "https://images.unsplash.com/photo-1558904541-efa843a96f01?q=80&w=1600"} 
                alt={service.title}
                className="w-full h-[400px] md:h-[500px] object-cover transform hover:scale-105 transition-transform duration-700"
              />
            </div>

            {/* Features */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-8">Unsere Leistungen im Detail</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {service.features?.map((feature, index) => (
                  <div key={index} className="flex items-start gap-4 p-6 bg-slate-50 rounded-2xl hover:bg-emerald-50 transition-colors group">
                    <div className="p-2 rounded-lg bg-white text-emerald-600 group-hover:bg-emerald-100 transition-colors">
                      <Check size={20} />
                    </div>
                    <span className="text-lg text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Info */}
            {service.additionalInfo && (
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Weitere Informationen</h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-slate-600 leading-relaxed">{service.additionalInfo}</p>
                </div>
              </div>
            )}

            {/* Benefits */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 md:p-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-8">Ihre Vorteile</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-white text-emerald-600 shadow-sm">
                      {benefit.icon}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{benefit.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Contact Card */}
            <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Kontakt & Beratung</h3>
              <div className="space-y-6">
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-emerald-50 transition-colors cursor-pointer">
                  <div className="p-3 rounded-lg bg-emerald-100 text-emerald-700">
                    <Phone size={24} />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900">Telefon</div>
                    <div className="text-slate-600">+4915735547097</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-emerald-50 transition-colors cursor-pointer">
                  <div className="p-3 rounded-lg bg-emerald-100 text-emerald-700">
                    <Mail size={24} />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900">E-Mail</div>
                    <div className="text-slate-600">info@galabaupalloshi.de</div>
                  </div>
                </div>
                
              </div>
            </div>

            {/* Quick Facts */}
            <div className="bg-slate-900 text-white rounded-3xl p-8">
              <h3 className="text-2xl font-bold mb-6">Schnell & einfach</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">Durchschnittliche Dauer</span>
                  <span className="font-bold">1-3 Tage</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">Reaktionszeit</span>
                  <span className="font-bold">24 Stunden</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">Gewährleistung</span>
                  <span className="font-bold">5 Jahre</span>
                </div>
              </div>
            </div>

            {/* Related Services */}
           
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-slate-900 to-emerald-900 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Bereit für Ihre {service.title}?
          </h2>
          <p className="text-xl text-emerald-100 mb-10 max-w-2xl mx-auto">
            Vereinbaren Sie jetzt Ihren persönlichen Beratungstermin und erhalten Sie ein unverbindliches Angebot.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <button className="bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-5 rounded-full font-bold text-lg transition-all flex items-center justify-center gap-3">
              <Phone size={24} /> Jetzt anrufen
            </button>
            <button className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white px-10 py-5 rounded-full font-bold text-lg transition-all flex items-center justify-center gap-3">
              <Mail size={24} /> E-Mail schreiben
            </button>
          </div>
        </div>
      </div>

      {/* Back to Services */}
      <div className="py-16 px-4">
        <div className="max-w-[1600px] mx-auto">
          <Link 
            to="/#services" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-emerald-700 transition-colors group"
          >
            <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            Zurück zu allen Leistungen
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;